import clsx from "clsx";
import React from "react";
import { ConfigProvider, Table } from "antd";
import type { ColumnsType, ColumnType, TableProps } from "antd/es/table";
import { useTranslation } from "react-i18next";

import Preloader from "~/components/Preloader";

import { DefaultTextCell } from "./Cells";
import { DefaultRowControl } from "./DefaultRowControl";

import s from "./TableV2.less";

const tableV2SortTheme = {
  components: {
    Table: {
      headerBorderRadius: 0,
      bodySortBg: "transparent",
      headerSortActiveBg: "#EAEBEB",
      headerSortHoverBg: "#EAEBEB",
      fixedHeaderSortActiveBg: "#EAEBEB",
    },
  },
};

/**
 * Пропсы компонента TableV2 - расширенной обертки над Ant Design Table
 * @template RecordType - тип записи в таблице, должен содержать id или key
 */
type TableV2Props<RecordType extends object> = Omit<
  TableProps<RecordType>,
  "columns" | "dataSource"
> & {
  /** Массив данных для отображения в таблице */
  dataSource?: RecordType[];
  /** Определение колонок таблицы. Если не указано, будут созданы из options */
  columns?: ColumnsType<RecordType>;
  /** Массив строковых ключей для автоматического создания колонок (используется если columns не указан) */
  options?: string[];
  /** Префикс для ключей переводов при автоматическом создании колонок (используется с options) */
  type?: string;
  /** Функция для рендеринга действий в каждой строке таблицы */
  rowActions?: (record: RecordType, index: number) => React.ReactNode;
  /** Обработчик просмотра деталей строки — показывает иконку ViewDetails */
  onViewDetails?: (record: RecordType) => void;
  /** Обработчик редактирования строки — показывает иконку Edit слева */
  onRowEdit?: (record: RecordType) => void;
  /** Обработчик удаления строки — показывает иконку Delete слева */
  onRowDelete?: (record: RecordType) => void;
  /** Обработчик скачивания — показывает иконку загрузки (между Edit и Delete) */
  onDownload?: (record: RecordType) => void;
  /** Подсказка для кнопки скачивания (title) */
  downloadButtonTitle?: string;
  /** Обработчик настроек строки — показывает иконку шестеренки */
  onRowSettings?: (record: RecordType) => void;
  /** Контент в начале блока действий (до стандартных иконок) */
  rowControlPrefix?:
    | React.ReactNode
    | ((record: RecordType) => React.ReactNode);
  /** Дополнительный контент в блоке действий (после стандартных иконок) */
  rowControlExtra?: React.ReactNode | ((record: RecordType) => React.ReactNode);
  /** Дополнительные настройки колонки с действиями (ширина, выравнивание и т.д.) */
  rowActionsColumn?: Omit<ColumnType<RecordType>, "render" | "fixed"> & {
    fixed?: "left" | "right";
  };
  /** Закреплять ли колонку с действиями при горизонтальной прокрутке */
  actionsFixed?: boolean;
  /** Размещение колонки с действиями: слева или справа (для rowActions) */
  actionsPlacement?: "right" | "left";
  /** Размещение колонки встроенных действий (onRowEdit, onDownload и т.д.). По умолчанию слева */
  builtinActionsPlacement?: "right" | "left";
  /** Чередование фона строк (нечётные #F7F8F8). Для таблиц со своей раскраской строк — false */
  stripedRows?: boolean;
  /** Массив ключей колонок, по которым разрешена сортировка */
  sortableColumns?: string[];
  /** Текущее поле сортировки */
  sortField?: string;
  /** Текущий тип сортировки: ASC (по возрастанию) или DESC (по убыванию) */
  sortType?: "ASC" | "DESC";
  /** Обработчик изменения сортировки */
  onSortChange?: (sortField?: string, sortType?: "ASC" | "DESC") => void;
  /** Дополнительные CSS классы для обертки таблицы */
  wrapperClassName?: string;
  /** Отключить горизонтальную прокрутку таблицы */
  noHorizontalScroll?: boolean;
  /** Функция-декоратор для строк таблицы, возвращает className и/или style для строки */
  rowDecorator?: (
    record: RecordType,
    index: number,
  ) => { className?: string; style?: React.CSSProperties };
  /** Имя поля записи для использования в качестве rowKey (если id/key повторяются) */
  customRowsKey?: string;
  /** Строка-заголовок группы (например раздел по GroupOne): первая колонка на всю ширину */
  isGroupRow?: (record: RecordType) => boolean;
  renderGroupRow?: (record: RecordType) => React.ReactNode;
};

/**
 * TableV2 - расширенная обертка над Ant Design Table с дополнительными возможностями
 *
 * Основные возможности:
 * - Автоматическое создание колонок из массива опций с поддержкой переводов
 * - Кастомные действия в строках с настраиваемым размещением
 * - Управление сортировкой с кастомными обработчиками
 * - Полосатый стиль для четных строк
 * - Гибкая настройка прокрутки
 *
 * @template RecordType - тип записи в таблице, должен содержать id или key для идентификации строк
 *
 * @example
 * ```tsx
 * <TableV2
 *   dataSource={users}
 *   columns={userColumns}
 *   rowActions={(record) => <Button onClick={() => edit(record)}>Edit</Button>}
 *   sortableColumns={['name', 'email']}
 *   onSortChange={(field, type) => handleSort(field, type)}
 * />
 * ```
 */
export const TableV2 = <
  RecordType extends { id?: string | number; key?: string | number },
>(
  props: TableV2Props<RecordType>,
) => {
  const {
    dataSource = [],
    columns,
    options = [],
    type = "",
    rowActions,
    onViewDetails,
    onRowEdit,
    onRowDelete,
    onDownload,
    downloadButtonTitle,
    onRowSettings,
    rowControlPrefix,
    rowControlExtra,
    rowActionsColumn,
    actionsFixed = false,
    actionsPlacement = "right",
    builtinActionsPlacement = "left",
    stripedRows = true,
    sortableColumns,
    sortField,
    sortType,
    onSortChange,
    wrapperClassName = "",
    noHorizontalScroll = false,
    rowDecorator,
    customRowsKey,
    isGroupRow,
    renderGroupRow,
    onChange: onTableChange,
    loading: tableLoading,
    ...tableProps
  } = props;
  const { t } = useTranslation();

  // Формирование колонок: используем переданные columns или создаем автоматически из options
  const resolvedColumns: ColumnsType<RecordType> = (
    columns ??
    options.map((option): ColumnType<RecordType> => {
      const translationKey = `${type}.${option}`;
      const translatedTitle = t(translationKey);
      const title =
        translatedTitle !== translationKey ? translatedTitle : option;

      return {
        title,
        dataIndex: option,
        key: option,
        render: (text: unknown) => <DefaultTextCell text={text} />,
      };
    })
  )
    .slice()
    .map((column) => {
      // Если у колонки нет render, добавляем DefaultTextCell
      if (!column.render) {
        return {
          ...column,
          render: (text: unknown) => <DefaultTextCell text={text} />,
        };
      }
      return column;
    });

  // Определяем размещение колонки с действиями
  const resolvedPlacement: ColumnType<RecordType>["fixed"] =
    actionsPlacement === "left" ? "left" : "right";

  const stopRowClickInControlsCell: React.HTMLAttributes<HTMLTableCellElement> =
    {
      onClick: (e) => e.stopPropagation(),
      onMouseDown: (e) => e.stopPropagation(),
    };

  // Добавляем колонку с действиями, если она указана
  if (rowActions) {
    const controlsColumn = {
      title: "",
      key: "row-controls",
      fixed: actionsFixed ? resolvedPlacement : undefined,
      ...rowActionsColumn,
      className: clsx(
        "tableV2RowActionsCell",
        s.rowControlsCell,
        rowActionsColumn?.className,
      ),
      onCell: (record, rowIndex) => {
        const prev = rowActionsColumn?.onCell;
        const base =
          typeof prev === "function" ? (prev(record, rowIndex) ?? {}) : {};
        return { ...base, ...stopRowClickInControlsCell };
      },
      render: (_: unknown, record: RecordType, index: number) =>
        rowActions(record, index),
    };

    // Вставляем колонку в начало или конец в зависимости от размещения
    if (resolvedPlacement === "left") {
      resolvedColumns.unshift(controlsColumn);
    } else {
      resolvedColumns.push(controlsColumn);
    }
  }

  const hasBuiltinActions =
    typeof onViewDetails === "function" ||
    typeof onRowEdit === "function" ||
    typeof onDownload === "function" ||
    typeof onRowDelete === "function" ||
    typeof onRowSettings === "function" ||
    rowControlPrefix != null ||
    rowControlExtra != null;

  if (hasBuiltinActions && !rowActions) {
    const resolvedBuiltinPlacement: ColumnType<RecordType>["fixed"] =
      builtinActionsPlacement === "left" ? "left" : "right";
    const builtinControlsColumn: ColumnType<RecordType> = {
      title: "",
      key: "row-builtin-controls",
      fixed: actionsFixed ? resolvedBuiltinPlacement : undefined,
      className: clsx(s.rowControlsCell, "tableV2RowActionsCell"),
      onHeaderCell: () => ({
        className: clsx(s.rowControlsCell, "tableV2RowActionsCell"),
      }),
      onCell: () => stopRowClickInControlsCell,
      render: (_: unknown, record: RecordType) => (
        <DefaultRowControl
          record={record}
          onViewDetails={onViewDetails}
          onRowEdit={onRowEdit}
          onRowDelete={onRowDelete}
          onDownload={onDownload}
          downloadButtonTitle={downloadButtonTitle}
          onRowSettings={onRowSettings}
          prefix={rowControlPrefix}
          extra={rowControlExtra}
        />
      ),
    };
    if (builtinActionsPlacement === "left") {
      resolvedColumns.unshift(builtinControlsColumn);
    } else {
      resolvedColumns.push(builtinControlsColumn);
    }
  }

  if (
    hasBuiltinActions &&
    !rowActions &&
    isGroupRow &&
    renderGroupRow &&
    resolvedColumns[0]?.key === "row-builtin-controls"
  ) {
    const totalCols = resolvedColumns.length;
    const firstCol = resolvedColumns[0];
    const origFirstRender = firstCol.render;

    resolvedColumns[0] = {
      ...firstCol,
      render: (...args: Parameters<NonNullable<typeof origFirstRender>>) => {
        const record = args[1] as RecordType;
        if (isGroupRow(record)) {
          return renderGroupRow(record);
        }
        return origFirstRender?.(...args) as React.ReactNode;
      },
      onCell: (record, rowIndex) => {
        const prev = firstCol.onCell;
        const base = typeof prev === "function" ? prev(record, rowIndex) : {};
        if (isGroupRow(record)) {
          return { ...base, ...stopRowClickInControlsCell, colSpan: totalCols };
        }
        return { ...base, ...stopRowClickInControlsCell };
      },
    };

    for (let i = 1; i < resolvedColumns.length; i++) {
      const col = resolvedColumns[i];
      const prevOnCell = col.onCell;
      resolvedColumns[i] = {
        ...col,
        onCell: (record, rowIndex) => {
          const base =
            typeof prevOnCell === "function"
              ? prevOnCell(record, rowIndex)
              : {};
          if (isGroupRow(record)) {
            return { ...base, colSpan: 0 };
          }
          return base;
        },
      };
    }
  }

  const rowKey = tableProps.rowKey ?? customRowsKey ?? "id";

  /**
   * Обработчик изменений таблицы (сортировка, фильтрация, пагинация)
   * Проксирует вызовы в переданный onTableChange и обрабатывает сортировку
   */
  const handleTableChange: TableProps<RecordType>["onChange"] = (
    pagination,
    filters,
    sorter,
    extra,
  ) => {
    // Проксируем все изменения в переданный обработчик
    if (onTableChange) {
      onTableChange(pagination, filters, sorter, extra);
    }

    if (!onSortChange) {
      return;
    }

    if (Array.isArray(sorter) && sorter.length === 0) {
      if (
        sortField &&
        sortType &&
        (!sortableColumns || sortableColumns.includes(String(sortField)))
      ) {
        onSortChange(undefined, undefined);
      }
      return;
    }

    const sorterSingle = Array.isArray(sorter)
      ? sorter.length === 1
        ? sorter[0]
        : undefined
      : sorter;

    if (!sorterSingle || (Array.isArray(sorter) && sorter.length > 1)) {
      return;
    }

    const order = sorterSingle.order;
    const rawField =
      sorterSingle.columnKey !== undefined && sorterSingle.columnKey !== null
        ? sorterSingle.columnKey
        : sorterSingle.field;

    let fieldKey: string | undefined;
    if (Array.isArray(rawField)) {
      const f = rawField[0];
      fieldKey = f !== undefined && f !== null ? String(f) : undefined;
    } else if (rawField !== undefined && rawField !== null) {
      fieldKey = String(rawField);
    }

    if (order !== "ascend" && order !== "descend") {
      if (
        sortField &&
        sortType &&
        (!sortableColumns || sortableColumns.includes(String(sortField))) &&
        (!fieldKey || !sortableColumns || sortableColumns.includes(fieldKey))
      ) {
        onSortChange(undefined, undefined);
      }
      return;
    }

    if (!fieldKey || (sortableColumns && !sortableColumns.includes(fieldKey))) {
      return;
    }

    const nextSortType = order === "ascend" ? "ASC" : "DESC";
    onSortChange(fieldKey, nextSortType);
  };

  const isEmpty = !dataSource?.length;
  const isLoading =
    tableLoading === true ||
    (typeof tableLoading === "object" &&
      tableLoading !== null &&
      tableLoading.spinning !== false);

  const mergedLoading: TableProps<RecordType>["loading"] =
    tableLoading === undefined || tableLoading === false
      ? false
      : tableLoading === true
        ? { spinning: true, indicator: <Preloader /> }
        : {
            ...tableLoading,
            indicator: tableLoading.indicator ?? <Preloader />,
          };
  const emptyStateContent =
    isEmpty && !isLoading ? (
      <div className={s.emptyState}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#tableV2EmptyIconClip)">
            <path
              d="M8 12C8.22666 12 8.4168 11.9232 8.5704 11.7696C8.724 11.616 8.80053 11.4261 8.8 11.2V8C8.8 7.77333 8.7232 7.58346 8.5696 7.4304C8.416 7.27733 8.22613 7.20053 8 7.2C7.77386 7.19946 7.584 7.27626 7.4304 7.4304C7.2768 7.58453 7.2 7.7744 7.2 8V11.2C7.2 11.4267 7.2768 11.6168 7.4304 11.7704C7.584 11.924 7.77386 12.0005 8 12ZM8 5.6C8.22666 5.6 8.4168 5.5232 8.5704 5.3696C8.724 5.216 8.80053 5.02613 8.8 4.8C8.79946 4.57387 8.72266 4.384 8.5696 4.2304C8.41653 4.0768 8.22666 4 8 4C7.77333 4 7.58346 4.0768 7.4304 4.2304C7.27733 4.384 7.20053 4.57387 7.2 4.8C7.19946 5.02613 7.27626 5.21627 7.4304 5.3704C7.58453 5.52453 7.7744 5.60107 8 5.6ZM8 16C6.89333 16 5.85333 15.7899 4.88 15.3696C3.90667 14.9493 3.06 14.3795 2.34 13.66C1.62 12.9405 1.05013 12.0939 0.630401 11.12C0.210668 10.1461 0.000534346 9.10613 1.01266e-06 8C-0.00053232 6.89386 0.209601 5.85387 0.630401 4.88C1.0512 3.90613 1.62107 3.05947 2.34 2.34C3.05893 1.62053 3.9056 1.05067 4.88 0.6304C5.8544 0.210133 6.8944 0 8 0C9.10559 0 10.1456 0.210133 11.12 0.6304C12.0944 1.05067 12.9411 1.62053 13.66 2.34C14.3789 3.05947 14.9491 3.90613 15.3704 4.88C15.7917 5.85387 16.0016 6.89386 16 8C15.9984 9.10613 15.7883 10.1461 15.3696 11.12C14.9509 12.0939 14.3811 12.9405 13.66 13.66C12.9389 14.3795 12.0923 14.9496 11.12 15.3704C10.1477 15.7912 9.10773 16.0011 8 16Z"
              fill="#495A64"
            />
          </g>
          <defs>
            <clipPath id="tableV2EmptyIconClip">
              <rect width="16" height="16" rx="8" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <span>{t("common.noData")}</span>
      </div>
    ) : isLoading ? (
      ""
    ) : (
      (tableProps.locale?.emptyText ?? t("common.noData"))
    );

  // Объединяем onRow из props с декоратором строк
  const getRowProps = (record: RecordType, index: number) => {
    const baseRowProps = props.onRow ? props.onRow(record, index) : {};
    const decoratorProps = rowDecorator
      ? rowDecorator(record, index)
      : { className: undefined, style: undefined };

    const hasRowClick = typeof baseRowProps.onClick === "function";

    return {
      ...baseRowProps,
      ...(hasRowClick ? { "data-row-clickable": true } : {}),
      className: clsx(
        baseRowProps.className,
        decoratorProps.className,
        isGroupRow?.(record) && s.tableGroupRow,
      ),
      style: { ...baseRowProps.style, ...decoratorProps.style },
    };
  };

  return (
    <div
      className={clsx(s.tableWrapper, wrapperClassName, {
        [s.tableWrapperNoStrip]: !stripedRows,
        [s.noHorizontalScroll]: noHorizontalScroll,
      })}
    >
      <ConfigProvider theme={tableV2SortTheme}>
        <Table
          {...tableProps}
          loading={mergedLoading}
          rowHoverable={tableProps.rowHoverable ?? false}
          columns={resolvedColumns}
          dataSource={dataSource}
          rowKey={rowKey}
          pagination={tableProps.pagination ?? false}
          size={tableProps.size ?? "small"}
          scroll={
            noHorizontalScroll
              ? (tableProps.scroll ?? undefined)
              : (tableProps.scroll ?? { x: "auto" })
          }
          locale={{
            ...tableProps.locale,
            emptyText: emptyStateContent,
          }}
          showSorterTooltip={tableProps.showSorterTooltip ?? false}
          onChange={handleTableChange}
          onRow={getRowProps}
        />
      </ConfigProvider>
    </div>
  );
};
